import React from "react";
import { useQuery } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { Loader2 } from "lucide-react";

import Badge from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AdminFoundersKeys,
  getAdminFounder,
} from "@/features/founders/api/admin-founders";
import {
  getLicenseeProgression,
  LevelsKeys,
} from "@/features/founders/api/levels";

interface FounderDetailsDialogProps {
  founderId: string | null;
  onClose: () => void;
}

const FounderDetailsDialog: React.FC<FounderDetailsDialogProps> = ({
  founderId,
  onClose,
}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [AdminFoundersKeys.GET_FOUNDER, founderId],
    queryFn: () => getAdminFounder(founderId!),
    enabled: !!founderId,
  });

  const { data: progression } = useQuery({
    queryKey: [LevelsKeys.GET_LICENSEE_PROGRESSION, founderId],
    queryFn: () => getLicenseeProgression(founderId!),
    enabled: !!founderId,
  });

  const renderValue = (
    value: any,
    type: "boolean" | "string" | "array" | "date" = "string",
  ) => {
    if (type === "boolean") {
      return (
        <Badge
          color={value ? "purple" : "lightGrey"}
          variant={value ? "full" : "outline"}
        >
          {value ? "Yes" : "No"}
        </Badge>
      );
    }
    if (type === "array") {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return <span className="text-muted-foreground">None</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <Badge key={index} variant="outline" className="text-xs">
              {item}
            </Badge>
          ))}
        </div>
      );
    }
    if (type === "date") {
      if (!value) {
        return <span className="text-muted-foreground">Not set</span>;
      }
      return new Date(value).toLocaleDateString();
    }
    return value || <span className="text-muted-foreground">Not set</span>;
  };

  return (
    <Dialog open={!!founderId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Founder Details</DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {error && (
          <div className="text-center py-8 text-destructive">
            Failed to load founder details
          </div>
        )}

        {data && (
          <ScrollArea className="h-[70vh] pr-2">
            <Tabs defaultValue="overview">
              <TabsList className="w-full">
                <TabsTrigger value="overview" className="flex-1">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="mentor" className="flex-1">
                  Mentor & Cohort
                </TabsTrigger>
                <TabsTrigger value="profile" className="flex-1">
                  Profile
                </TabsTrigger>
                <TabsTrigger value="billing-ids" className="flex-1">
                  Billing & IDs
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Name
                    </label>
                    <p className="mt-1">
                      {renderValue(`${data.firstName} ${data.lastName}`)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Level (Backend)
                      </label>
                      <p className="mt-1">
                        {renderValue(
                          progression?.level
                            ? progression.level
                            : data.level || "starter",
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Stripe Active
                      </label>
                      <div className="mt-1">
                        {renderValue(data.stripeActive, "boolean")}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Email
                    </label>
                    <p className="mt-1">{renderValue(data.email)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Country
                    </label>
                    <p className="mt-1">{renderValue(data.country)}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Onboarded
                      </label>
                      <div className="mt-1">
                        {renderValue(data.onboarded, "boolean")}
                      </div>
                    </div>
                    {data.nonAIFounder && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Legacy Founder
                        </label>
                        <div className="mt-1">
                          {renderValue(true, "boolean")}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Account Activated
                      </label>
                      <div className="mt-1">
                        {renderValue(data.accountActivated, "boolean")}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Subscription Active
                      </label>
                      <div className="mt-1">
                        {renderValue(data.subscriptionActive, "boolean")}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Lead Finder Enabled
                      </label>
                      <div className="mt-1">
                        {renderValue(data.leadFinderEnabled, "boolean")}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Subscription Valid Until
                    </label>
                    <p className="mt-1">
                      {renderValue(data.subscriptionValidUntil, "date")}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Downpayment Required
                      </label>
                      <div className="mt-1">
                        {renderValue(data.isDownpayment, "boolean")}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Downpayment Paid
                      </label>
                      <div className="mt-1">
                        {renderValue(data.isDownpaymentPaid, "boolean")}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Welcome Email Sent
                    </label>
                    <div className="mt-1">
                      {renderValue(data.welcomeEmailSent, "boolean")}
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="mentor">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Mentor
                    </label>
                    <p className="mt-1">
                      {renderValue(
                        data.mentor
                          ? typeof data.mentor === "string"
                            ? data.mentor
                            : `${data.mentor?.firstName} ${data.mentor?.lastName}`
                          : null,
                      )}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Cohort
                    </label>
                    <div className="mt-1">
                      {typeof data.cohort !== "string" &&
                        data.cohort?.startDate && (
                          <p className="text-base font-medium">
                            {formatInTimeZone(
                              data.cohort.startDate,
                              "America/New_York",
                              "MMM d, yyyy",
                            )}
                          </p>
                        )}
                      <p className="text-xs text-muted-foreground">
                        {renderValue(
                          typeof data.cohort === "string"
                            ? data.cohort
                            : `#${data.cohort?.number}`,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="profile">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Niche (Who)
                    </label>
                    <p className="mt-1">{renderValue(data.nicheWho)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Niche (What)
                    </label>
                    <p className="mt-1">{renderValue(data.nicheWhat)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Business Category
                    </label>
                    <div className="mt-1">
                      {renderValue(data.businessCategory, "array")}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Referral Code
                    </label>
                    <p className="mt-1">{renderValue(data.referralCode)}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="billing-ids">
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Stripe Customer ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.stripeCustomerId)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Stripe Account ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.stripeId)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Stripe Subscription ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.stripeSubscriptionId)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Hash
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.hash)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Internal ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data._id)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Auth0 ID
                      </label>
                      <p className="mt-1 font-mono text-sm break-all">
                        {renderValue(data.auth0Id)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Created At
                        </label>
                        <p className="mt-1">
                          {renderValue(data.createdAt, "date")}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Updated At
                        </label>
                        <p className="mt-1">
                          {renderValue(data.updatedAt, "date")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FounderDetailsDialog;
